import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@heroui/react'
import { useSearchParams } from 'react-router'
import { usePostHog } from '@posthog/react'
import type { ReturnsLineItem } from '@itsrever/rever-api'
import { RefundAction } from '@itsrever/returns-api-types'
import { useTheme } from '@/ui/theme'
import { useTranslate } from '@/hooks/useTranslate'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useProductsBrands } from '@/hooks/use-products-brands'
import { useCalculate, useNavigate, usePages, useRefundMethods } from '@/hooks'
import { ReturnMethod, setReturnMethod } from '@/redux/return/return-slice'
import {
  USE_MULTILABEL_RETURN_ID,
  useFeatureFlag
} from '@/hooks/use-feature-flag'
import { isNonReturnableProduct } from './helpers/is-non-returnable-product'
import { isWFPClaim, isWFPOrder, WFP_SKU } from '@/utils/worry-free-purchase'
import { DesktopFullCatalog } from '@/app/full-catalog/desktop-full-catalog'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import { NonReturnableItemCard } from './item-card/non-returnable-item-card'
import { ItemCard } from './item-card/item-card'
import { HoffItemCard } from './item-card/hoff-item-card'
import { MisakoItemCard } from './item-card/misako-item-card'
import { WfpBanner } from './wfp-banner'
import { OrderDetails } from './order-details'

export const ItemSelection = () => {
  const { t } = useTranslation()
  const { translate } = useTranslate()

  const posthog = usePostHog()

  const { navigateRelative, navigateBack } = useNavigate()
  const { pages } = usePages()

  const [searchParams] = useSearchParams()

  const isFullCatalogOpen = searchParams.get('catalog') === 'true'
  const lineItemIdParam = searchParams.get('lineItemId')

  const theme = useTheme()

  const dispatch = useAppDispatch()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const ecommerceId = settings.ecommerce_id

  const isInStore = settings.in_store_settings?.is_enabled

  const isHoff = settings.ecommerce_group === 'hoff'
  const isMisako = settings.ecommerce_group === 'misako'

  const { order, checkout_id: checkoutId } = useAppSelector(
    (store) => store.returnsApi.order.response
  )

  const returnData = useAppSelector((store) => store.return)

  const products = useAppSelector((store) => store.order.orderProducts)

  const itemsToReturn = useAppSelector((store) => store.return.returnedItems)

  const { isFlagEnabled: isMultilabelReturnEnabled } = useFeatureFlag(
    USE_MULTILABEL_RETURN_ID
  )

  const { data } = useProductsBrands({
    orderNumber: order?.identification?.customer_printed_order_id,
    email: order?.customer?.email,
    ecommerceId: isMultilabelReturnEnabled ? ecommerceId : undefined
  })

  const shouldBlockProductReturnByBrand = (product?: ReturnsLineItem) => {
    if (!itemsToReturn.length || !product) return false

    const firstSelectedReturnItem = itemsToReturn[0]
    const productsBrands = data?.products_and_brands ?? []

    const firstItemProductId = order?.line_items?.find(
      (item) => item.id === firstSelectedReturnItem.lineItemId
    )?.product?.id

    const firstItemBrand = productsBrands.find(
      (p) => p.product_id === firstItemProductId
    )?.brand_name

    if (!firstItemBrand) return false

    // Check if the product belongs to a different brand
    return productsBrands.some(
      (p) =>
        p.product_id === product.product?.id && p.brand_name !== firstItemBrand
    )
  }

  const { callRefundMethods, loadingRefundMethods } = useRefundMethods(
    handleNavigateNextPage
  )

  const { callCalculate, loadingCalculate } = useCalculate()

  function handleNavigateNextPage() {
    const onlyNotReceived = itemsToReturn.every(
      (retItem) => retItem.item.product_return_reason == 'NOT_RECEIVED'
    )

    const onlyExchanges = itemsToReturn.every(
      (item) => item.item.action === RefundAction.RefundAction_TO_EXCHANGE
    )

    if (isWFPClaim(order?.line_items ?? [], itemsToReturn)) {
      dispatch(
        setReturnMethod({
          returnMethod: ReturnMethod.NoReturnMethod,
          userSelectedCarrier: '',
          userSelectedProvider: ''
        })
      )

      if (onlyExchanges) {
        callCalculate(ReturnMethod.NoReturnMethod, returnData.exchangeAddress)
        return
      }

      navigateRelative(pages.RefundMethod)
      return
    }

    if (isInStore) {
      dispatch(
        setReturnMethod({
          returnMethod: onlyNotReceived
            ? ReturnMethod.NoReturnMethod
            : ReturnMethod.InStore,
          userSelectedCarrier: '',
          userSelectedProvider: ''
        })
      )

      if (onlyExchanges) {
        callCalculate(
          onlyNotReceived ? ReturnMethod.NoReturnMethod : ReturnMethod.InStore,
          returnData.exchangeAddress
        )

        return
      }

      navigateRelative(pages.RefundMethod)

      return
    }

    if (onlyNotReceived) {
      if (onlyExchanges) {
        callCalculate(ReturnMethod.NoReturnMethod, returnData.returnAddress)
      } else {
        navigateRelative(pages.RefundMethod)
      }
    } else {
      navigateRelative(pages.ReturnAddress)
    }
  }

  const onContinueReturn = () => {
    callRefundMethods(returnData.returnAddress)
  }

  useEffect(() => {
    posthog?.capture('Initial', {
      isFullCatalogEnabled: settings.exchange_flow === 'FULL',
      exchange_flow: settings.exchange_flow,
      checkout_id: checkoutId
    })
  }, [posthog, settings, checkoutId])

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText
  const rounded = theme.borderRadius

  if (isHoff) {
    return (
      <>
        {loadingCalculate || loadingRefundMethods ? (
          <LoadingOverlay loading />
        ) : null}
        <div className="mx-auto flex min-h-0 w-full max-w-screen-xl flex-1 flex-col overflow-hidden bg-white px-4 pt-6">
          <OrderDetails order={order} />
          {isWFPOrder(order?.line_items ?? []) && <WfpBanner />}
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto rounded-xl bg-white/80">
            <h1
              className="w-full pb-1 pt-6 text-lg font-normal text-neutral-800 lg:text-lg"
              dangerouslySetInnerHTML={{
                __html: translate('item_selection_title')
              }}
            />
            <div className="mt-4 flex flex-col border-b-1 border-solid border-black">
              {products.map((prod, i) => {
                if (prod.product.product?.sku === WFP_SKU) return null

                return (
                  <Fragment key={i}>
                    {isNonReturnableProduct(prod) ? (
                      <NonReturnableItemCard item={prod.product} />
                    ) : (
                      <HoffItemCard
                        lineItem={prod.product}
                        subProductId={prod.subProductId}
                      />
                    )}
                  </Fragment>
                )
              })}
            </div>
            <div className="mb-10 mt-6 flex w-full">
              <button
                id="item-selection-continue-button"
                onClick={onContinueReturn}
                disabled={itemsToReturn.length === 0}
                className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 text-sm text-white disabled:bg-neutral-500"
              >
                {translate('item_selection_button_continue')}
              </button>
            </div>
          </div>
        </div>
        {isFullCatalogOpen && lineItemIdParam ? (
          <DesktopFullCatalog
            currentReturnedItemId={lineItemIdParam}
            onContinue={navigateBack}
          />
        ) : null}
      </>
    )
  }

  if (isMisako) {
    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title

    return (
      <>
        {loadingCalculate || loadingRefundMethods ? (
          <LoadingOverlay loading />
        ) : null}
        <div className="flex h-full min-h-full flex-1 flex-col gap-4 bg-white p-4 md:px-10 md:py-6">
          <div className="mb-auto flex w-full items-end">
            {logoUrl ? (
              <div className="mr-auto">
                <img
                  style={
                    {
                      '--logo-desktop-height': '1.5rem',
                      '--logo-mobile-height': '1.2rem'
                    } as React.CSSProperties
                  }
                  width={285}
                  height={70}
                  src={logoUrl}
                  alt={title}
                  className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                />
              </div>
            ) : null}
            <div className="flex items-center gap-5">
              <a
                href="https://www.misako.com/pages/wishlist"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                  width={20}
                  height={20}
                />
              </a>
              <a
                href="https://www.misako.com/account"
                target="_blank"
                rel="noreferrer"
              >
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                    stroke="rgb(21, 21, 21)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                    stroke="rgb(21, 21, 21)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </a>
              <a
                href="https://www.misako.com/collections/top-ventas"
                target="_blank"
                rel="noreferrer"
              >
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                    stroke="rgb(21, 21, 21)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </a>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <h1
              className="mt-5 pb-5 text-lg font-[300] tracking-wider text-neutral-600 lg:text-xl"
              dangerouslySetInnerHTML={{
                __html: translate('item_selection_title')
              }}
            />
            <OrderDetails order={order} />
            {isWFPOrder(order?.line_items ?? []) && <WfpBanner />}
            <div className="mt-6 flex flex-wrap gap-4 border-solid border-grey-3 md:gap-8">
              {products.map((prod, i) => {
                if (prod.product.product?.sku === WFP_SKU) return null

                return (
                  <Fragment key={i}>
                    {isNonReturnableProduct(prod) ? (
                      <NonReturnableItemCard item={prod.product} />
                    ) : (
                      <MisakoItemCard
                        lineItem={prod.product}
                        subProductId={prod.subProductId}
                      />
                    )}
                  </Fragment>
                )
              })}
            </div>
            <div className="mt-auto flex w-full justify-center pt-8">
              <div className="w-full pb-4 md:max-w-[180px]">
                <Button
                  id="item-selection-continue-button"
                  onPress={onContinueReturn}
                  isDisabled={itemsToReturn.length === 0}
                  fullWidth
                  variant="solid"
                  radius="sm"
                  style={{
                    backgroundColor: color,
                    color: textColor,
                    borderRadius: rounded
                  }}
                >
                  <span className="text-[13px] font-[300] uppercase tracking-wide">
                    {translate('item_selection_button_continue')}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        {isFullCatalogOpen && lineItemIdParam ? (
          <DesktopFullCatalog
            currentReturnedItemId={lineItemIdParam}
            onContinue={navigateBack}
          />
        ) : null}
      </>
    )
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <main className="flex-1 overflow-y-auto bg-[#F2F2F2]">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center space-y-8 p-4 md:p-6">
          <h1
            className="text-center text-xl font-normal"
            dangerouslySetInnerHTML={{
              __html: translate('item_selection_title')
            }}
          />
          <div className="w-full space-y-4">
            <OrderDetails order={order} />
            {isWFPOrder(order?.line_items ?? []) && <WfpBanner />}
            <div>
              {products.length > 0 ? (
                <ul className="space-y-3">
                  {products.map((prod, i) => {
                    if (prod.product.product?.sku === WFP_SKU) return null

                    return (
                      <li key={i}>
                        {isNonReturnableProduct(prod) ? (
                          <NonReturnableItemCard item={prod.product} />
                        ) : (
                          <ItemCard
                            lineItem={prod.product}
                            subProductId={prod.subProductId}
                            isBlocked={shouldBlockProductReturnByBrand(
                              prod.product
                            )}
                          />
                        )}
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="py-20">
                  <p className="mx-auto max-w-48 text-center text-sm font-normal text-neutral-500">
                    {t('initial_selection.no_items_available')}
                  </p>
                </div>
              )}
            </div>
          </div>
          <Button
            type="submit"
            variant="solid"
            radius="sm"
            size="lg"
            fullWidth
            style={{
              backgroundColor: color,
              color: textColor,
              borderRadius: rounded,
              maxWidth: '20rem'
            }}
            isLoading={loadingCalculate || loadingRefundMethods}
            isDisabled={itemsToReturn.length === 0}
            onPress={onContinueReturn}
          >
            {translate('item_selection_button_continue')}
          </Button>
        </div>
      </main>
      {isFullCatalogOpen && lineItemIdParam ? (
        <DesktopFullCatalog
          currentReturnedItemId={lineItemIdParam}
          onContinue={navigateBack}
        />
      ) : null}
    </div>
  )
}
